import { Box, useMediaQuery, useTheme } from "@mui/material";
import {
  useState
} from "react";
import { useTranslation } from "react-i18next";
// eslint-disable-next-line import/no-unresolved
import type { Swiper as SwiperType } from "swiper";
/* eslint-disable import/no-unresolved */
import {
  Autoplay,
  Mousewheel,
  Scrollbar
} from "swiper/modules";
import { SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/scrollbar";
/* eslint-enable import/no-unresolved */
import { StyledCarousel } from "./Carousel.styles";
import { PaginationCarousel } from "../../components/PaginationCarousel/PaginationCarousel.component";
import { useSweepState,
  useType,
  getLevelLabel
} from "../../hooks";
import { claculateAriaLabel } from "../../utils/explore";

type IProps<T extends { title: string }> = {
  carouselData: T[];
  renderElement: (element: T,isCarouselView:boolean, index?: number) => JSX.Element;
  slidesPerView?: number | "auto";
  centeredSlides?: boolean;
  spaceBetween?: number;
  isBoxShadowEnabled?: boolean;
  loopSlides?: boolean;
  hasPagination: boolean;
  hasPaginationSweeperBullets: boolean;
  hideSweeperBulletsRight?: boolean;
  title: string;
  renderElementBigcarouselGridView?: (element: T,isCarouselView:boolean) => JSX.Element;
  pauseOnMouseEnter?: boolean;
  autoplay?:boolean
};

// eslint-disable-next-line no-empty-pattern
export const Carousel = <T extends { title: string },>({
  carouselData,
  renderElement,
  slidesPerView = "auto",
  hasPagination,
  hasPaginationSweeperBullets,
  title,
  centeredSlides = false,
  loopSlides = false,
  spaceBetween = 0,
  hideSweeperBulletsRight = false,
  pauseOnMouseEnter = false,
  renderElementBigcarouselGridView,
  autoplay=true
}: IProps<T>) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const {
    setSwiperInstance,
    swiperInstance,
    autoSweep,
    handleAutoSweep
  } = useSweepState();

  const [isCarouselView, setIsCarouselView] = useState(true);
  const [carouselIndexStatus, setCarouselIndexStatus] = useState({
    index: 0,
    isBeginning: true,
    isEnd: false
  });

  const onSetActiveSlide = (state: SwiperType) => {
    setCarouselIndexStatus({
      index: state.activeIndex,
      isBeginning: state.isBeginning,
      isEnd: state.isEnd
    });

    setSwiperInstance(state);
  };
  const handleGridViewSwitch = () => {
    setIsCarouselView(prev => !prev);
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const ariaLabels = carouselData.map((el:any) =>
    claculateAriaLabel(el, useType, (typology) => getLevelLabel(typology, t), t)
  );

  return (
    <>
      <Box>
        <PaginationCarousel
          idScope={ title }
          autoSweep={ autoSweep }
          handleAutoSweep={ handleAutoSweep }
          swiperInstance={ swiperInstance }
          activeIndex={ carouselIndexStatus.index || 0 }
          isBeginning={ carouselIndexStatus.isBeginning }
          isEnd={ carouselIndexStatus.isEnd }
          handleGridViewSwitch={ handleGridViewSwitch }
          isCarouselView={ isCarouselView }
          hasPagination={ hasPagination }
          hasPaginationSweeperBullets={ hasPaginationSweeperBullets }
          hasGreedView={ true }
          hideSweeperBulletsRight={ hideSweeperBulletsRight }
          items={ carouselData }
        />
      </Box>
      { isCarouselView ? (
        <Box sx={ { minWidth: 0 } }>
          <StyledCarousel
            onResize={ onSetActiveSlide }
            a11y={ { enabled: false } }
            role="region"
            aria-roledescription="carousel"
            aria-label={ title === "bigcarousel" ? "Bigbanner Carousel" : title }
            autoplay={
              autoplay
                ? {
                  delay: 7000,
                  disableOnInteraction: true,
                  pauseOnMouseEnter: pauseOnMouseEnter
                }
                : false
            }
            slidesPerView={ slidesPerView }
            loop={ loopSlides }
            centeredSlides={ centeredSlides }
            spaceBetween={ spaceBetween }
            direction="horizontal"
            mousewheel={ {
              forceToAxis: true,
              releaseOnEdges: true
            } }
            scrollbar={ { draggable: true } }
            modules={ [Autoplay, Mousewheel, Scrollbar ] }
            observer={ true }
            observeParents={ true }
            observeSlideChildren={ true }
            simulateTouch={ true }
            onSwiper={ (swiper: SwiperType) =>{
              setSwiperInstance(swiper);
              onSetActiveSlide(swiper);
            } }
            onUpdate={ (swiper: SwiperType) => onSetActiveSlide(swiper) }
            onSlideChange={ onSetActiveSlide }
            onTransitionEnd={ onSetActiveSlide }
            onActiveIndexChange={ onSetActiveSlide }
          >
            { /*eslint-disable-next-line @typescript-eslint/no-explicit-any*/ }
            { carouselData.map((element:any, i: number) => {
              return (
                <SwiperSlide
                  onFocus={ (e) => {
                    if (e.target !== e.currentTarget) return;
                    swiperInstance?.autoplay?.stop?.();
                    swiperInstance?.slideToLoop(i, 0, true);
                  } }
                  aria-label={ `${t("content",{ index:i+1,length:carouselData.length })} - ${ariaLabels[i]}` }
                  role="group"
                  style={ isMobile ?{ width: "100%", boxSizing: "border-box" }:{  } }
                  key={ "carousel-slide-" + i }
                  tabIndex={ 0 }
                >
                  { renderElement(element,true) }
                </SwiperSlide>
              );
            }) }
          </StyledCarousel>
        </Box>
      ) : (
        <div style={ { display: "flex", flexWrap: "wrap", flexGrow: 1, gap: "0.7rem" } }>
          { carouselData.map((element, i: number) => {
            return (
              <div
                // className="basic-grid-card"
                style={ isMobile ? { width:"24.5rem", boxSizing: "content-box" }:{  boxSizing: "content-box" } }
                key={ "basic-card-" + i }
                role="region"
                aria-label={  !isCarouselView ? element.title : undefined }
              >
                { renderElementBigcarouselGridView ? renderElementBigcarouselGridView(element,false) :renderElement(element,false) }
              </div>
            );
          }) }
        </div>
      ) }
    </>
  );
};
